import React, { useState } from 'react';
import { useWorldStore, type OnlinePlayer } from '../stores/worldStore';
import { Users, ChevronUp, ChevronDown } from 'lucide-react';
import './OnlineUsersBar.css';
import { openPlayerCard } from './safety/openSafety';
import { getPresenceKey } from '../services/realtimeManager';

const OnlineUsersBar: React.FC = () => {
  const onlinePlayers: OnlinePlayer[] = useWorldStore((s: any) => s.onlinePlayers);
  const [expanded, setExpanded] = useState(false);

  if (onlinePlayers.length === 0) return null;

  // Generate a color from the player name for the avatar circle
  const nameColor = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 60%, 50%)`;
  };

  const getInitials = (name: string) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  const maxStacked = 5;
  const visiblePlayers = expanded ? onlinePlayers : onlinePlayers.slice(0, maxStacked);
  const overflowCount = onlinePlayers.length - maxStacked;

  return (
    <div
      className={`online-users-bar ${expanded ? 'expanded' : ''}`}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button className="online-users-toggle" onClick={() => setExpanded(!expanded)}>
        <Users size={14} />
        <span className="online-count">{onlinePlayers.length}</span>
        {expanded ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
      </button>

      <div className="online-users-content">
        {!expanded && (
          <div className="online-users-stacked">
            {visiblePlayers.map((player, i) => (
              <div
                key={player.id}
                className="online-avatar-circle"
                style={{
                  backgroundColor: nameColor(player.userName),
                  zIndex: onlinePlayers.length - i,
                }}
                title={player.userName}
              >
                {getInitials(player.userName)}
              </div>
            ))}
            {overflowCount > 0 && (
              <div className="online-avatar-circle overflow-circle" title={`${overflowCount} more`}>
                +{overflowCount}
              </div>
            )}
          </div>
        )}

        {expanded && (
          <div className="online-users-list">
            {onlinePlayers.map((player) => (
              <div key={player.id} className="online-user-row"
                role={player.id !== getPresenceKey() ? 'button' : undefined}
                style={player.id !== getPresenceKey() ? { cursor: 'pointer' } : undefined}
                title={player.id !== getPresenceKey() ? `${player.userName}: mute, block or report` : undefined}
                onClick={() => openPlayerCard({ userId: player.userId || null, key: player.id, name: player.userName })}>
                <div
                  className="online-avatar-circle small"
                  style={{ backgroundColor: nameColor(player.userName) }}
                >
                  {getInitials(player.userName)}
                </div>
                <span className="online-user-name">{player.userName}</span>
                <span className="online-status-dot" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OnlineUsersBar;
